import { useEffect, useMemo, useState, useCallback, Fragment } from 'react';
import { apiFetch, apiImageUrl } from '../../utils/streamApi';
import IndustrialLayout, { toast } from './IndustrialLayout';

const isoDay = (d) => d.toISOString().slice(0, 10);            // YYYY-MM-DD

// Evidence thumbnail for one known-seen sighting.
function Snap({ entryId }) {
  const [src, setSrc] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let live = true;
    apiImageUrl(`/known-seen/${encodeURIComponent(entryId)}/snapshot`).then(u => live && setSrc(u));
    return () => { live = false; };
  }, [entryId]);
  if (err || !src) return <div style={{ width: 84, height: 60, borderRadius: 6, background: '#12171B', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>📷</div>;
  return <img src={src} alt="" onError={() => setErr(true)} style={{ width: 84, height: 60, objectFit: 'cover', borderRadius: 6, display: 'block' }} />;
}
const csvCell = (v) => {
  const s = String(v ?? '');
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

// Attendance report derived from known-face sightings (no new ML) — per person
// per day: first-seen (in), last-seen (out), hours on site, sightings, cameras.
export default function IndustrialAttendancePage() {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [q, setQ]             = useState('');
  const [from, setFrom]       = useState(() => isoDay(new Date(Date.now() - 7 * 86400 * 1000)));
  const [to, setTo]           = useState(() => isoDay(new Date()));
  const [openKey, setOpenKey] = useState(null);   // "name|date" of the expanded row
  const [detail, setDetail]   = useState({});     // key → sightings[] | 'loading'
  const [lb, setLb]           = useState(null);   // entry_id of enlarged snapshot, or null
  const [cameras, setCameras] = useState([]);     // for the Entry/Exit gate setup
  const [hasRoles, setHasRoles] = useState(false);// are any Entry/Exit gates configured?
  const [showGates, setShowGates] = useState(false);

  useEffect(() => {
    apiFetch('/ui/cameras').then(r => r.json()).then(d => setCameras(d.cameras || [])).catch(() => {});
  }, []);

  const saveRole = async (idx, role) => {
    try {
      const r = await apiFetch(`/cameras/${idx}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ cam_config: { attendance_role: role } }) });
      if (!r.ok) throw new Error();
      setCameras(cs => cs.map(c => c.idx === idx ? { ...c, cam_config: { ...(c.cam_config || {}), attendance_role: role } } : c));
      toast('Gate role saved.', 'ok');
      load();   // recompute attendance with the new roles
    } catch { toast('Save failed.', 'err'); }
  };

  const toggleRow = useCallback((r) => {
    const key = `${r.name}|${r.date}`;
    if (openKey === key) { setOpenKey(null); return; }
    setOpenKey(key);
    if (detail[key]) return;
    setDetail(d => ({ ...d, [key]: 'loading' }));
    const day = r.date.replace(/-/g, '');
    apiFetch(`/ui/attendance/detail?name=${encodeURIComponent(r.name)}&date_from=${day}&date_to=${day}`)
      .then(res => res.json())
      .then(d => setDetail(m => ({ ...m, [key]: d.sightings || [] })))
      .catch(() => setDetail(m => ({ ...m, [key]: [] })));
  }, [openKey, detail]);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const df = from.replace(/-/g, ''), dt = to.replace(/-/g, '');
      const d = await apiFetch(`/ui/attendance?date_from=${df}&date_to=${dt}`).then(r => r.json());
      setRows(d.attendance || []);
      setHasRoles(!!d.has_roles);
    } catch {
      setError('Failed to load attendance');
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(
    () => (q ? rows.filter(r => r.name.toLowerCase().includes(q.toLowerCase())) : rows),
    [rows, q]
  );
  const people = useMemo(() => new Set(rows.map(r => r.name)).size, [rows]);

  const exportCsv = () => {
    if (!filtered.length) { toast('Nothing to export.', 'warn'); return; }
    const head = ['Date', 'Person', 'In', 'In gate', 'Out', 'Out gate', 'Hours', 'Approx', 'Sightings', 'Cameras'];
    const lines = [head.join(',')];
    filtered.forEach(r => lines.push([
      r.date, r.name, r.first_seen, r.in_gate || '', r.last_seen, r.out_gate || '', r.hours,
      (r.in_approx || r.out_approx) ? 'yes' : '', r.sightings, (r.cameras || []).join('; '),
    ].map(csvCell).join(',')));
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `attendance_${from}_to_${to}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast(`Exported ${filtered.length} rows.`);
  };

  return (
    <IndustrialLayout
      eyebrow="Workforce"
      title="Attendance"
      subtitle={loading ? 'LOADING…' : error ? error : `${people} PEOPLE · ${rows.length} PERSON-DAYS · ${from} → ${to}`}
      banner={
        <div className="ind-banner info">
          {hasRoles
            ? <>Clock-<b>in</b> = earliest sighting on an <b>Entry</b> gate; clock-<b>out</b> = latest on an <b>Exit</b> gate (across all gates). <b>≈</b> marks a time approximated from any-camera sighting when a gate sighting was missing.</>
            : <>Derived from enrolled known-face sightings — first &amp; last time each person was recognised per day. Tag <b>Entry/Exit gates</b> (Gate setup) for proper in/out. Presence evidence, not a turnstile clock.</>}
        </div>
      }>
      {/* Controls always visible so the range can be changed even with no data */}
      <div className="ind-card" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span className="ind-eye" style={{ margin: 0 }}>Range</span>
        <input type="date" value={from} max={to} onChange={e => setFrom(e.target.value)} style={DATE} />
        <span style={{ color: 'var(--ind-tx3)' }}>→</span>
        <input type="date" value={to} min={from} onChange={e => setTo(e.target.value)} style={DATE} />
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search person…"
          style={{ flex: 1, minWidth: 180, maxWidth: 280, padding: '8px 12px', borderRadius: 8, border: '1px solid var(--ind-line)', background: 'var(--ind-ink)', color: 'var(--ind-tx)', fontSize: 13 }} />
        <button className="ind-btn" onClick={() => setShowGates(s => !s)}>{showGates ? '▾' : '▸'} Gate setup</button>
        <button className="ind-btn" onClick={exportCsv} disabled={!filtered.length}>⤓ Export CSV</button>
      </div>

      {showGates && (
        <div className="ind-card">
          <div className="ind-eye" style={{ marginBottom: 6 }}>Entry / Exit gates</div>
          <p style={{ fontSize: 12, color: 'var(--ind-tx3)', margin: '0 0 10px' }}>
            Tag each camera. Clock-<b>in</b> = earliest sighting on any <b>Entry</b> gate; clock-<b>out</b> = latest
            on any <b>Exit</b> gate (works across multiple gates). <b>Both</b> = a shared gate. Leave as <b>None</b> to
            exclude a camera. With no gates tagged, attendance falls back to first/last sighting on any camera.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 8 }}>
            {cameras.map(c => (
              <div key={c.idx} style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between',
                border: '1px solid var(--ind-line)', borderRadius: 8, padding: '6px 10px' }}>
                <span style={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>🎥 {c.name || `Camera ${c.idx}`}</span>
                <select value={c.cam_config?.attendance_role || ''} onChange={e => saveRole(c.idx, e.target.value)} style={DATE}>
                  <option value="">None</option>
                  <option value="entry">Entry</option>
                  <option value="exit">Exit</option>
                  <option value="both">Both</option>
                </select>
              </div>
            ))}
            {cameras.length === 0 && <span className="ind-empty">No cameras.</span>}
          </div>
        </div>
      )}

      {loading ? <div className="ind-empty">Building report…</div>
        : error ? <div className="ind-empty">{error}</div>
        : !rows.length ? <div className="ind-empty">No known-face sightings in {from} → {to}.</div>
        : (
        <>
          <div className="ind-card" style={{ padding: 0, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--ind-tx3)', fontFamily: 'var(--ind-mono)', fontSize: 10, letterSpacing: '.08em' }}>
                  <th style={TH}>DATE</th><th style={TH}>PERSON</th><th style={TH}>IN</th><th style={TH}>OUT</th>
                  <th style={TH}>HOURS</th><th style={TH}>SIGHTINGS</th><th style={TH}>CAMERAS</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => {
                  const key = `${r.name}|${r.date}`;
                  const open = openKey === key;
                  const sights = detail[key];
                  return (
                    <Fragment key={`${key}-${i}`}>
                      <tr onClick={() => toggleRow(r)}
                        style={{ borderTop: '1px solid var(--ind-line)', cursor: 'pointer', background: open ? 'var(--ind-wash)' : 'transparent' }}>
                        <td style={TD}><span style={{ fontFamily: 'var(--ind-mono)', fontSize: 11 }}>{open ? '▾ ' : '▸ '}{r.date}</span></td>
                        <td style={{ ...TD, fontWeight: 600 }}>
                          {r.name}
                          {r.restricted && <span title="Seen outside allowed hours — presence recorded, but a RESTRICTED alert fired"
                            style={{ marginLeft: 8, fontSize: 9, fontWeight: 700, letterSpacing: '.05em', padding: '2px 6px', borderRadius: 4,
                              background: 'rgba(214,44,44,.12)', color: '#d62c2c', border: '1px solid rgba(214,44,44,.3)' }}>OUT-OF-HOURS</span>}
                        </td>
                        <td style={MONO}>
                          {r.first_seen}{r.in_approx && <span title="No Entry-gate sighting — approximated from first sighting anywhere" style={APPROX}>≈</span>}
                          {r.in_gate && <div style={GATE}>{r.in_gate}</div>}
                        </td>
                        <td style={MONO}>
                          {r.last_seen}{r.out_approx && <span title="No Exit-gate sighting — approximated from last sighting anywhere" style={APPROX}>≈</span>}
                          {r.out_gate && <div style={GATE}>{r.out_gate}</div>}
                        </td>
                        <td style={MONO}>{r.hours}</td>
                        <td style={MONO}>{r.sightings}</td>
                        <td style={{ ...TD, color: 'var(--ind-tx3)', fontSize: 11 }}>{(r.cameras || []).join(', ')}</td>
                      </tr>
                      {open && (
                        <tr>
                          <td colSpan={7} style={{ padding: '10px 14px', background: 'var(--ind-wash)', borderTop: '1px solid var(--ind-line)' }}>
                            {sights === 'loading' ? <div className="ind-empty" style={{ padding: 14 }}>Loading sightings…</div>
                              : !sights || !sights.length ? <div className="ind-empty" style={{ padding: 14 }}>No individual sightings recorded.</div>
                              : (
                                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                                  {sights.map(s => (
                                    <div key={s.entry_id} style={{ width: 84 }}>
                                      <div style={{ cursor: 'zoom-in' }} onClick={() => setLb(s.entry_id)} title="Click to enlarge">
                                        <Snap entryId={s.entry_id} />
                                      </div>
                                      <div style={{ fontFamily: 'var(--ind-mono)', fontSize: 10, color: 'var(--ind-tx2)', marginTop: 3 }}>{s.time}</div>
                                      <div style={{ fontSize: 9, color: 'var(--ind-tx3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.cam_name}{s.face_score != null ? ` · ${s.face_score}` : ''}</div>
                                    </div>
                                  ))}
                                </div>
                              )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {lb && (
        <div onClick={() => setLb(null)}
          style={{ position: 'fixed', inset: 0, zIndex: 3000, background: 'rgba(0,0,0,.88)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, cursor: 'zoom-out' }}>
          <SnapLarge entryId={lb} />
          <button onClick={e => { e.stopPropagation(); setLb(null); }}
            style={{ position: 'absolute', top: 16, right: 20, background: 'transparent', border: 'none', color: '#fff', fontSize: 26, cursor: 'pointer' }}>✕</button>
        </div>
      )}
    </IndustrialLayout>
  );
}

// Full-size sighting snapshot for the lightbox.
function SnapLarge({ entryId }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let live = true;
    apiImageUrl(`/known-seen/${encodeURIComponent(entryId)}/snapshot`).then(u => live && setSrc(u));
    return () => { live = false; };
  }, [entryId]);
  if (!src) return <div style={{ color: '#fff' }}>loading…</div>;
  return <img src={src} alt="" onClick={e => e.stopPropagation()}
    style={{ maxWidth: '95vw', maxHeight: '92vh', objectFit: 'contain', borderRadius: 8, boxShadow: '0 10px 60px rgba(0,0,0,.6)' }} />;
}

const TH   = { padding: '11px 14px' };
const TD   = { padding: '10px 14px' };
const MONO = { padding: '10px 14px', fontFamily: 'var(--ind-mono)', fontSize: 12 };
const APPROX = { marginLeft: 5, color: '#f59e0b', fontWeight: 700, cursor: 'help' };
const GATE = { fontSize: 9, color: 'var(--ind-tx3)', fontFamily: 'inherit', marginTop: 2 };
const DATE = { padding: '7px 10px', borderRadius: 8, border: '1px solid var(--ind-line)', background: 'var(--ind-ink)', color: 'var(--ind-tx)', fontSize: 13 };
